import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Database, Upload, Search, Edit3, Save, X, Loader2, FileSpreadsheet,
  RefreshCw, Info, Check, ChevronDown, ChevronUp, Clock, AlertTriangle
} from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

/* Compute contract months left from date_fdc (contract end date). */
function monthsLeft(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const now = new Date();
  return Math.round(((d - now) / (1000 * 60 * 60 * 24 * 30.44)) * 10) / 10;
}
function formatDuree(m) {
  if (m == null) return "—";
  if (m < 0)  return `Expiré ${Math.abs(Math.round(m))} m.`;
  if (m < 12) return `${Math.round(m)} mois`;
  const y = Math.floor(m / 12);
  const r = Math.round(m - y * 12);
  return r > 0 ? `${y} an${y>1?"s":""} ${r} m` : `${y} an${y>1?"s":""}`;
}
function dureeTone(m) {
  if (m == null)  return "text-slate-400 bg-slate-50";
  if (m < 0)      return "text-slate-500 bg-slate-100 line-through";
  if (m < 6)      return "text-rose-700 bg-rose-100";
  if (m < 12)     return "text-amber-700 bg-amber-100";
  if (m < 24)     return "text-blue-700 bg-blue-100";
  return "text-emerald-700 bg-emerald-100";
}

const COLUMN_LABELS = {
  marque: "MARQUE", type: "TYPE", logo: "Logo",
  corners: "Corners", nom_enseigne: "Nom enseigne", nom_compte: "Nom du compte",
  dr_short: "DR", dr_nom_complet: "Nom complet DR", region: "Nom de la Région",
  bm_short: "BM (Responsable secteur)", bm_nom_complet: "Nom complet du BM (Responsable secteur)",
  secteur: "Nom du secteur",
  surface_m2: "Surface en m2 du Corner", taux_commission: "Taux de commission",
  date_fdc: "Date FDC", duree_contrat_mois: "Durée contrat (mois)",
  duree_restante: "Durée restante",
  propriete_inox: "Propriété Inox", propriete_vitrine: "Propriété Vitrine",
  propriete_bac_wok: "Propriété Bac wok", propriete_habillage: "Propriété habillage",
  run_rate: "Run Rate", adresse: "Adresse", date_ouverture: "Date d'ouverture", type_zone: "Type de zone",
  adherent: "Adhérent(e)", adherent_contact: "Numéro / Mail adhérent(e)",
  directeur: "Directeur(trice)", directeur_contact: "Numéro / Mail Directeur(trice)",
  resp_qualite: "Resp Qualité", resp_qualite_contact: "Numéro / Mail Resp qualité",
  resp_fichier: "Resp Fichier", resp_fichier_contact: "Numéro / Mail Resp Fichier",
  comptable: "Comptable", comptable_contact: "Numéro/ Mail Comptable",
  visites_planifiees_mois: "Visites planifiées / mois",
};

// Default visible = Excel-order first 12 columns (MARQUE → Nom du secteur) + Durée restante
const DEFAULT_VISIBLE = ["marque", "type", "corners", "nom_enseigne", "nom_compte", "dr_short", "region", "bm_short", "secteur", "visites_planifiees_mois", "duree_restante", "adresse"];

export default function BaseInfo() {
  const [items, setItems] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE);
  const [editing, setEditing] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState("");
  const [showColPicker, setShowColPicker] = useState(false);
  const [sortBy, setSortBy] = useState({ col: "canonical_name", dir: "asc" });
  const [f, setF] = useState({ dr: "", bm: "", secteur: "", marque: "", enseigne: "" });
  const [contratFilter, setContratFilter] = useState(""); // "" | "expired" | "lt6" | "lt12" | "lt24"

  // Attach computed duree_restante (in months) on each item
  const itemsWithDuree = useMemo(
    () => items.map(it => ({ ...it, duree_restante: monthsLeft(it.date_fdc) })),
    [items]
  );

  const uniq = (col) => Array.from(new Set(items.map(it => (it[col] || "").toString().trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const opts = useMemo(() => ({
    dr: uniq("dr_short"), bm: uniq("bm_short"), secteur: uniq("secteur"),
    marque: uniq("marque"), enseigne: uniq("nom_enseigne"),
  }), [items]);
  const activeCount = Object.values(f).filter(Boolean).length + (contratFilter ? 1 : 0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/base-info");
      setItems(data.items || []); setColumns(data.columns || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Chargement impossible"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const runImport = async (file) => {
    const url = file ? await uploadFile(file) : importUrl;
    if (!url) return toast.error("URL requise");
    if (!confirm("⚠️ Cet import va REMPLACER toutes les fiches Info_corner. Continuer ?")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/base-info/import", { file_url: url, replace_all: true });
      toast.success(`${data.imported} fiches importées · ${data.stores_linked} liés · ${data.stores_created} créés · ${data.archived} archivés`);
      setShowImportModal(false); setImportUrl(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setImporting(false);
  };

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "info_corner");
    try {
      const { data } = await api.post("/uploads", fd);
      return data.url;
    } catch {
      toast.error("Upload impossible — utilisez une URL publique"); return null;
    }
  };

  const startEdit = (id, field, currentValue) => { setEditing({ id, field }); setEditValue(currentValue || ""); };
  const cancelEdit = () => { setEditing(null); setEditValue(""); };
  const saveEdit = async () => {
    if (!editing) return;
    try {
      const { data } = await api.patch(`/admin/base-info/${editing.id}`, { updates: { [editing.field]: editValue || null } });
      setItems(prev => prev.map(it => it.id === editing.id ? data : it));
      toast.success("Modifié");
      cancelEdit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (item) => {
    if (!confirm(`Supprimer « ${item.canonical_name} » ?`)) return;
    try { await api.delete(`/admin/base-info/${item.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = itemsWithDuree.filter(it => {
      const _has = (csv, val) => !csv || csv.split(",").includes(val || "");
      if (!_has(f.dr, it.dr_short)) return false;
      if (!_has(f.bm, it.bm_short)) return false;
      if (!_has(f.secteur, it.secteur)) return false;
      if (!_has(f.marque, it.marque)) return false;
      if (!_has(f.enseigne, it.nom_enseigne)) return false;
      const m = it.duree_restante;
      if (contratFilter === "expired" && !(m != null && m < 0)) return false;
      if (contratFilter === "lt6"     && !(m != null && m >= 0 && m < 6)) return false;
      if (contratFilter === "lt12"    && !(m != null && m >= 0 && m < 12)) return false;
      if (contratFilter === "lt24"    && !(m != null && m >= 0 && m < 24)) return false;
      if (!s) return true;
      return Object.values(it).some(v => (v || "").toString().toLowerCase().includes(s));
    });
    if (sortBy.col) {
      const numericCols = new Set(["duree_restante", "duree_contrat_mois", "surface_m2", "run_rate", "taux_commission"]);
      const isNum = numericCols.has(sortBy.col);
      list.sort((a, b) => {
        if (isNum) {
          const av = a[sortBy.col]; const bv = b[sortBy.col];
          const aa = av == null || isNaN(av) ? Infinity : Number(av);
          const bb = bv == null || isNaN(bv) ? Infinity : Number(bv);
          return sortBy.dir === "asc" ? aa - bb : bb - aa;
        }
        const av = (a[sortBy.col] || "").toString();
        const bv = (b[sortBy.col] || "").toString();
        return sortBy.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return list;
  }, [itemsWithDuree, q, f, contratFilter, sortBy]);

  return (
    <div className="space-y-4" data-testid="base-info-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-600">
            <Database className="w-3.5 h-3.5"/> Source de vérité
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Base Info Magasins</h1>
          <p className="text-slate-500 mt-1 text-sm">Fiche officielle des {items.length} corners — modifiable ligne par ligne. Toute autre page se met à jour automatiquement.</p>
        </div>
        <button onClick={() => setShowImportModal(true)} data-testid="base-info-import"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 text-white font-semibold text-sm shadow">
          <Upload className="w-4 h-4"/> Importer Info_corner.xlsx
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher (ville, enseigne, DR, BM…)…"
                 className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm"/>
        </div>
        <button onClick={() => setShowColPicker(!showColPicker)} data-testid="base-info-cols"
                className="px-3 py-2.5 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold flex items-center gap-1">
          Colonnes ({visibleCols.length}/{columns.length}) <ChevronDown className="w-3.5 h-3.5"/>
        </button>
        <button onClick={load} className="px-3 py-2.5 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold flex items-center gap-1">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/>
        </button>
      </div>

      {/* Filtres DR / BM / Secteur / Marque / Enseigne */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-wrap items-end gap-2" data-testid="base-info-filters">
        {[
          { k: "dr",       label: "DR",       tid: "bi-flt-dr" },
          { k: "bm",       label: "BM",       tid: "bi-flt-bm" },
          { k: "secteur",  label: "Secteur",  tid: "bi-flt-secteur" },
          { k: "marque",   label: "Marque",   tid: "bi-flt-marque" },
          { k: "enseigne", label: "Enseigne", tid: "bi-flt-enseigne" },
        ].map(({ k, label, tid }) => (
          <div key={k} className="flex flex-col">
            <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">{label}</span>
            <FilterMultiSelect label={label} placeholder="Tous" testid={tid} size="sm" minWidth={130} className="max-w-[220px]"
                               options={opts[k]} value={(f[k] || "").split(",").filter(Boolean)}
                               onChange={arr => setF(v => ({ ...v, [k]: arr.join(",") }))}/>
          </div>
        ))}
        {activeCount > 0 && (
          <button onClick={() => { setF({ dr: "", bm: "", secteur: "", marque: "", enseigne: "" }); setContratFilter(""); }}
                  data-testid="bi-flt-reset"
                  className="text-[11px] text-rose-600 font-semibold hover:underline flex items-center gap-1 mb-1.5 ml-1">
            <X className="w-3 h-3"/> Réinitialiser ({activeCount})
          </button>
        )}
        <div className="ml-auto text-[11px] font-semibold text-slate-500 mb-1.5" data-testid="bi-flt-count">
          {filtered.length} / {items.length} fiches
        </div>
      </div>

      {/* Quick filters : Durée de contrat + tri échéance */}
      <div className="flex flex-wrap items-center gap-2" data-testid="bi-contrat-chips">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <Clock className="w-3 h-3"/> Contrats :
        </span>
        {[
          { k: "",        label: "Tous" },
          { k: "expired", label: "Expirés",   tone: "bg-slate-100 text-slate-700 border-slate-200" },
          { k: "lt6",     label: "< 6 mois",  tone: "bg-rose-50 text-rose-700 border-rose-200" },
          { k: "lt12",    label: "< 12 mois", tone: "bg-amber-50 text-amber-700 border-amber-200" },
          { k: "lt24",    label: "< 24 mois", tone: "bg-blue-50 text-blue-700 border-blue-200" },
        ].map(({ k, label, tone }) => {
          const on = contratFilter === k;
          return (
            <button key={k} onClick={() => setContratFilter(k)}
                    data-testid={`bi-contrat-${k || "all"}`}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition
                                ${on ? "bg-blue-600 text-white border-blue-600"
                                     : tone || "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
              {label}
            </button>
          );
        })}
        <span className="mx-1 h-4 w-px bg-slate-200"/>
        <button onClick={() => setSortBy({ col: "duree_restante", dir: "asc" })}
                data-testid="bi-sort-duree-asc"
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${sortBy.col==="duree_restante" && sortBy.dir==="asc" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"} flex items-center gap-1`}>
          <AlertTriangle className="w-3 h-3"/> Trier : + proche → + ancien
        </button>
        <button onClick={() => setSortBy({ col: "duree_restante", dir: "desc" })}
                data-testid="bi-sort-duree-desc"
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${sortBy.col==="duree_restante" && sortBy.dir==="desc" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
          + ancien → + proche
        </button>
      </div>

      {showColPicker && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Sélection des colonnes visibles</div>
          <div className="flex flex-wrap gap-1.5">
            {columns.map(c => {
              const on = visibleCols.includes(c);
              return (
                <button key={c} onClick={() => setVisibleCols(v => on ? v.filter(x => x !== c) : [...v, c])}
                        className={`text-[11px] px-2 py-1 rounded-lg border font-semibold ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>
                  {COLUMN_LABELS[c] || c}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-auto max-h-[70vh]">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
            <tr className="text-left text-[10px] font-bold tracking-wider text-slate-500 uppercase">
              {visibleCols.map(c => (
                <th key={c} className="px-3 py-2 whitespace-nowrap cursor-pointer hover:bg-slate-100" onClick={() => setSortBy(s => ({ col: c, dir: s.col === c && s.dir === "asc" ? "desc" : "asc" }))}>
                  <div className="flex items-center gap-1">
                    {COLUMN_LABELS[c] || c}
                    {sortBy.col === c && (sortBy.dir === "asc" ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>)}
                  </div>
                </th>
              ))}
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={visibleCols.length + 1} className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline"/></td></tr>}
            {!loading && filtered.map(it => (
              <tr key={it.id} data-testid={`base-info-row-${it.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                {visibleCols.map(c => {
                  const isEditing = editing?.id === it.id && editing?.field === c;
                  // Special renderer: Durée restante = read-only colored pill
                  if (c === "duree_restante") {
                    const m = it.duree_restante;
                    return (
                      <td key={c} className="px-3 py-1.5 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${dureeTone(m)}`}
                              title={it.date_fdc ? `Fin de contrat : ${String(it.date_fdc).slice(0,10)}` : ""}>
                          <Clock className="w-3 h-3"/>{formatDuree(m)}
                        </span>
                      </td>
                    );
                  }
                  return (
                    <td key={c} className="px-3 py-1.5 whitespace-nowrap group" onDoubleClick={() => startEdit(it.id, c, it[c])}>
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
                                 onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                                 className="px-2 py-0.5 rounded border border-blue-400 text-xs w-full"/>
                          <button onClick={saveEdit} className="text-emerald-600 hover:bg-emerald-50 rounded p-0.5"><Check className="w-3 h-3"/></button>
                          <button onClick={cancelEdit} className="text-rose-500 hover:bg-rose-50 rounded p-0.5"><X className="w-3 h-3"/></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 min-w-[80px]">
                          <span className="text-slate-800">{it[c] || <span className="text-slate-300">—</span>}</span>
                          <button onClick={() => startEdit(it.id, c, it[c])} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition">
                            <Edit3 className="w-3 h-3"/>
                          </button>
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-1.5">
                  <button onClick={() => del(it)} className="text-rose-500 hover:bg-rose-50 rounded p-1">
                    <X className="w-3.5 h-3.5"/>
                  </button>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={visibleCols.length + 1} className="text-center py-12 text-slate-400 italic">
                {items.length === 0 ? "Aucune fiche encore. Importez Info_corner.xlsx." : `Aucun résultat pour « ${q} »`}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-slate-500 flex items-center gap-1">
        <Info className="w-3 h-3"/> Double-clic (ou icône crayon) sur une cellule pour éditer. Entrée = enregistrer · Échap = annuler.
      </div>

      {showImportModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setShowImportModal(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="font-display text-xl font-semibold">Importer Info_corner.xlsx</h2>
                <p className="text-xs text-slate-500 mt-0.5">Remplace toutes les fiches actuelles. Les magasins non présents seront archivés.</p>
              </div>
              <button onClick={() => setShowImportModal(false)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block cursor-pointer">
                <input type="file" accept=".xlsx,.xls" hidden onChange={e => { const f = e.target.files?.[0]; if (f) runImport(f); }}/>
                <div className="border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 rounded-xl p-6 text-center transition-colors">
                  <FileSpreadsheet className="w-8 h-8 text-slate-400 mx-auto"/>
                  <div className="text-sm font-semibold text-slate-700 mt-2">Cliquez pour choisir un fichier .xlsx</div>
                  <div className="text-[11px] text-slate-500 mt-1">Ou renseignez une URL publique ci-dessous</div>
                </div>
              </label>
              <div className="text-center text-xs text-slate-400 font-semibold">OU</div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">URL publique du fichier</label>
                <input value={importUrl} onChange={e => setImportUrl(e.target.value)} data-testid="base-info-import-url"
                       placeholder="https://…/Info_corner.xlsx"
                       className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <button onClick={() => setShowImportModal(false)} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
              <button onClick={() => runImport(null)} disabled={importing || !importUrl} data-testid="base-info-import-submit"
                      className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1 disabled:opacity-40">
                {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>} Importer depuis l'URL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
